import type { ReactNode } from 'react';
import { useEffect, useMemo, useState } from 'react';
import type { Toast } from 'react-hot-toast';
import toast from 'react-hot-toast';

import { NotificationBanner } from './NotificationBanner';

export interface PromiseNotificationBannerProps {
  type: 'promise';
  promise: Promise<unknown>;
  successMessage: ReactNode;
  errorMessage: ReactNode;
  loadingMessage?: ReactNode;
  /** Reject values matching this dismiss the toast silently (no error shown). */
  suppressOn?: (error: unknown) => boolean;
  toast: Toast;
}

export const PromiseNotificationBanner = (
  props: PromiseNotificationBannerProps
) => {
  const { promise, suppressOn } = props;
  const toastId = props.toast.id;
  const [state, setState] = useState<'error' | 'loading' | 'success'>(
    'loading'
  );

  useEffect(() => {
    setState('loading');
    promise
      .then(() => {
        setState('success');
      })
      .catch((error: unknown) => {
        // A "no-op" outcome (e.g. user-cancelled change-request confirm) should
        // produce no toast at all — dismiss silently rather than show an error.
        if (suppressOn?.(error)) {
          toast.dismiss(toastId);
        } else {
          setState('error');
        }
      });
  }, [promise, suppressOn, toastId]);

  const content = useMemo(() => {
    switch (state) {
      case 'loading':
        return props.loadingMessage ?? 'Saving changes...';
      case 'success':
        return props.successMessage;
      case 'error':
        return props.errorMessage;
    }
  }, [state, props.errorMessage, props.successMessage, props.loadingMessage]);

  return (
    <NotificationBanner
      toast={props.toast}
      type={state}
      content={content}
      durationMs={state === 'success' ? 3500 : Infinity}
      // Dismissable in any terminal state (success as well as error), not just
      // error — parity with the plain success/error toasts. This lets the E2E
      // notification helper clear the toast with a click instead of blocking on
      // the ~3.5s success auto-dismiss, whose cumulative cost tipped heavy
      // multi-step specs past the per-test timeout on the slower V3 lane
      // (RSP-6569). Loading stays non-dismissable — it's mid-flight.
      dismissable={state !== 'loading'}
    />
  );
};
